'use strict';

import { AutomationActionCollection } from '../../Collections/Automation/AutomationActionCollection';
import { Utilities } from '../../Utilities/Utilities';
import type { AutomationActionTriggerData } from './Automation';
import { Automation } from './Automation';
import { Entity } from '../Entity';

/**
 * Trigger types
 */

/**
 * Action types
 */
export const AUTOMATION_ACTION_TYPE = {
  CREATE_ACTIVITY: 'create_activity',
  SEND_EMAIL: 'send_email',
  SEND_SMS: 'send_sms',
  SEND_MAIL: 'send_mail',
  SEND_RINGLESS_VM: 'send_ringless_vm',
  UPDATE_LEAD: 'update_lead',
  SYNC_CONTACT: 'sync_contact',
  SYNC_REMOVE_CONTACT: 'sync_remove_contact',
  FLOW_UPSERT: 'flow_upsert',
} as const;

export type AUTOMATION_ACTION_TYPE =
  (typeof AUTOMATION_ACTION_TYPE)[keyof typeof AUTOMATION_ACTION_TYPE];

export interface AutomationActionPayload {
  type?: AUTOMATION_ACTION_TYPE;
  data?: any;
  parentAction?: number;
  startDelayDays?: number;
  startDelayTime?: any;
  position?: number;
  triggerCriteria?: AutomationActionTriggerData[];
}

export class AutomationAction extends Entity {
  protected id;
  protected type: AUTOMATION_ACTION_TYPE;
  protected data = null;
  protected parentAction: AutomationAction = null;
  protected startDelayDays: number = null;
  protected startDelayTime: Date = null;
  protected position = null;
  protected automation: Automation;
  protected triggerCriteria: AutomationActionTriggerData[];
  protected subActions: AutomationActionCollection;

  constructor() {
    super();
  }

  public hydrate(data: any) {
    this.id = data.id;
    this.type = data.type;
    this.triggerCriteria = data.triggerCriteria;
    this.position = data.position;
    this.startDelayDays = data.startDelayDays;
    this.subActions = new AutomationActionCollection();

    if (data.startDelayTime) {
      const startDelayTime = Utilities.dateTimeStringToDateTime(data.startDelayTime);
      this.startDelayTime = startDelayTime;
    }

    if (data.automation) {
      const automation = new Automation();
      automation.hydrate(data.automation);

      this.automation = automation;
    }

    if (data.parentAction) {
      const parentAction = new AutomationAction();
      parentAction.hydrate(data.parentAction);

      this.parentAction = parentAction;
    }

    if (data.subActions) {
      data.subActions.forEach((subActionData) => {
        const subAction = new AutomationAction();
        subAction.hydrate(subActionData);
        this.subActions.addItem(subAction);
      });
    }

    this.data = data.data;
  }

  public getId() {
    return this.id;
  }

  public setId(id: number) {
    this.id = id;
  }

  public getType(): AUTOMATION_ACTION_TYPE {
    return this.type;
  }

  public setType(type: AUTOMATION_ACTION_TYPE) {
    this.type = type;
    return this;
  }

  public getStartDelayTime() {
    return this.startDelayTime;
  }

  public setStartDelayTime(startDelayTime: Date) {
    this.startDelayTime = startDelayTime;
    return this;
  }

  public getStartDelayDays() {
    return this.startDelayDays;
  }

  public setStartDelayDays(startDelayDays: number) {
    this.startDelayDays = startDelayDays;
    return this;
  }

  public getPosition() {
    return this.position;
  }

  public setPosition(position: number) {
    this.position = position;
    return this;
  }

  public getSubActions() {
    return this.subActions;
  }

  public setSubActions(subActions: AutomationActionCollection) {
    this.subActions = subActions;
    return this;
  }

  public getAutomation() {
    return this.automation;
  }

  public setAutomation(automation: Automation) {
    this.automation = automation;
    return this;
  }

  public getParentAction() {
    return this.parentAction;
  }

  public setParentAction(parentAction: AutomationAction) {
    this.parentAction = parentAction;
    return this;
  }

  public getData() {
    return this.data;
  }

  public setData(data: any) {
    this.data = data;
    return this;
  }

  public getTriggerCriteria() {
    return this.triggerCriteria;
  }

  public setTriggerCriteria(triggerCriteria: any) {
    this.triggerCriteria = triggerCriteria;
    return this;
  }
}
